import React from 'react';
import I18n from 'i18n-js';
import URLHelpers from '../shared/url_helpers';
import { get } from 'lodash';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import {
  Listing,
  Negotiation,
  core_apimessages_NegotiationParty as NegotiationParty,
} from '../../schemas/rql';
import { IOfferPrices } from '@reverbdotcom/commons/src/offers/negotiation_types';
import {
  lastOffer,
  offerQuantity,
} from './negotiation_getters';

interface IProps {
  prices: IOfferPrices;
  originalPrices: IOfferPrices;
  negotiation: Negotiation;
  listing: Listing;
  party: NegotiationParty;
}

function lastOfferInitiator(listing: Listing, negotiation: Negotiation, party: NegotiationParty) {
  if (party === NegotiationParty.BUYER) {
    return {
      name: listing.shop.name,
      linkOpen: `<a href="${URLHelpers.shopPath(listing.shop.slug)}">`,
      linkClose: '</a>',
    };
  }

  const buyerShop = negotiation.buyer.shop;
  if (buyerShop) {
    return { name: buyerShop.name, linkOpen: `<a href="${URLHelpers.shopPath(buyerShop.slug)}">`, linkClose: '</a>' };
  }

  return { name: negotiation.buyer.name, linkOpen: '', linkClose: '' };
}

function summaryTotalOrAddress(prices: IOfferPrices, negotiation: Negotiation, party: NegotiationParty) {
  if (party === NegotiationParty.BUYER) {
    const lastOfferTotal = get(prices, 'totalPrice.display');

    return (
      <p className="offers-form__negotiation-summary__details">
        {I18n.t('discovery.offers.offerTotal', { offerTotal: lastOfferTotal })}
      </p>
    );
  }

  const address = get(negotiation, 'shippingLocation.displayLocation');
  if (party === NegotiationParty.SELLER && !!address) {
    return (
      <p className="offers-form__negotiation-summary__details">
        {I18n.t('discovery.offers.shipToAddress', { address })}
      </p>
    );
  }

  return null;
}

function sellerAlerts(negotiation: Negotiation) {
  const location = get(negotiation, 'shippingLocation.displayLocation');
  const buyerName = negotiation.buyer.name;

  if (!location) {
    return (
      <div className="mt-2">
        <RCAlertBox type="info">
          {I18n.t('discovery.offers.alert.unknownLocation', { buyerName })}
        </RCAlertBox>
      </div>
    );
  }

  if (negotiation.sellerShippingWarning) {
    return (
      <div className="mt-2">
        <RCAlertBox type="warning">
          {negotiation.sellerShippingWarning}
        </RCAlertBox>
      </div>
    );
  }

  return null;
}

function renderQuantityDescription(negotiation: Negotiation) {
  const quantity = offerQuantity(lastOffer(negotiation));

  if (quantity > 1) {
    return (
      <p className="offers-form__negotiation-summary__quantity">
        {I18n.t('discovery.offers.summaryWithQuantity', { quantity })}
      </p>
    );
  }

  return null;
}

function renderConvertedFrom(prices: IOfferPrices, originalPrices: IOfferPrices) {
  if (originalPrices && prices.price.currency !== originalPrices.price.currency) {
    const price = get(originalPrices, 'price.display');
    const priceText = `${price} <span class="size-80 weight-normal">${get(originalPrices, 'price.currency')}</span>`;
    const text = I18n.t('discovery.offers.convertedFrom', {
      price: priceText,
      shipping: get(originalPrices, 'shippingPrice.display'),
    });

    return (
      <p className="offers-form__negotiation-summary__sub-price">
        <SanitizedRender html={text} />
      </p>
    );
  }
}

export default function NegotiationSummary({ prices, originalPrices, listing, negotiation, party }: IProps) {
  const shippingPrice = get(prices, 'shippingPrice.display', '');
  const offerAmount = get(prices, 'price.display', '');
  const { linkOpen, linkClose, name } = lastOfferInitiator(listing, negotiation, party);

  return (
    <div className="offers-form__negotiation-summary">
      <p className="offers-form__negotiation-summary__title">
        <SanitizedRender
          html={I18n.t('discovery.offers.offerFromPartyName', { linkOpen, linkClose, partyName: name })}
        />
      </p>
      <div className="offers-form__negotiation-summary__price-block">
        <p className="offers-form__negotiation-summary__price">
          {I18n.t('discovery.offers.negotiationSum', { offerAmount, shippingPrice })}
          {negotiation.taxIncluded &&
            <span className="offers-form__negotiation-summary__tax-hint">({negotiation.taxIncludedHint})</span>
          }
        </p>
        {renderConvertedFrom(prices, originalPrices)}
      </div>
      {renderQuantityDescription(negotiation)}
      {summaryTotalOrAddress(prices, negotiation, party)}
      {party === NegotiationParty.SELLER &&
        sellerAlerts(negotiation)
      }
    </div>
  );
}
